import { InteractionRequiredAuthError, type AccountInfo } from "@azure/msal-browser";
import { loginRequest } from '../../authConfig';
import IAuth from 'system/types/interfaces/IAuth';

let timeOutReference: NodeJS.Timeout | null = null;

// Pages that fire off several API calls at once on mount (e.g. AskAidenDocumentsPage loading
// URLs and documents in parallel) would otherwise each start their own acquireTokenSilent call.
// Sharing the in-flight promise means concurrent callers await the same result instead of
// racing separate silent requests - one of which failing must not force a real interactive
// login when the token is otherwise perfectly valid.
let inFlightTokenRequest: Promise<string | void> | null = null;

const authentication: IAuth = {
  context: undefined,
  get account(): AccountInfo | void {
    return this.context?.accounts[0]
  },
  async getToken(): Promise<string | void> {
    if (!this.context || !this.account) {
      return;
    }

    if (inFlightTokenRequest) {
      return inFlightTokenRequest;
    }

    const context = this.context;
    const account = this.account;

    inFlightTokenRequest = context.instance
      .acquireTokenSilent({ scopes: loginRequest.scopes, account })
      .then(result => result?.accessToken)
      .catch(e => {
        console.error(e);
        // Only a genuine "the current session can't get this token silently" error should
        // force the user through an interactive login - anything else (a transient network
        // blip, a hiccup in the silent iframe) is not a reason to log an already-authenticated
        // user out and back in.
        if (e instanceof InteractionRequiredAuthError) {
          context.instance.loginRedirect({ scopes: loginRequest.scopes });
        }
      })
      .finally(() => {
        inFlightTokenRequest = null;
      });

    return inFlightTokenRequest;
  },
  get isNotInitialized() {
    return this.context?.inProgress !== 'none' || !(this.context?.instance as any).initialized;
  },
  get sessionTimeout(): number {
    var result = parseInt(process.env.REACT_APP_SESSION_TIMEOUT ?? '');
    if (isNaN(result)) {
      // Default timeout = 30 minutes
      result = 30;
    }

    return result;
  },
  sessionIgnoreActions: [
    'metric/metrics',
    'metric/age',
    'metric/filters'
  ],
  shouldIgnoreSessionExtension(action: string): boolean {
    for (const ignoreAction of this.sessionIgnoreActions) {
      if (action.match(new RegExp(`^${ignoreAction}`))) {
        return true;
      }
    }

    return false;
  },
  extendSession(action: string) {
    if (this.shouldIgnoreSessionExtension(action)) {
      return;
    }

    if (timeOutReference) {
      clearTimeout(timeOutReference);
      timeOutReference = null;
    }

    timeOutReference = setTimeout(() => {
      this.logout();
    }, this.sessionTimeout * 60000);
  },
  logout() {
    this.logoutInitialized = true;
    this.context?.instance.logoutRedirect();
  },
  logoutInitialized: false
}

export default authentication;